export const meta = {
  title: 'Loyalty: points, tiers, and vouchers that come back',
  updatedAt: '2026-07-17',
  related: ['pos', 'promotions', 'getting-started'],
};

Loyalty runs itself: customers earn points on every collected ticket, points climb them through tiers, and tiers earn them automatic discounts. Your job is the fun part — deciding when to hand out vouchers.

## How points are earned

The moment a ticket is collected and paid, the customer earns **1 point per RM 1 spent** (rounded down — RM 49.90 is 49 points). Only signed-in customers earn; a walk-in ticket with no customer account earns nothing, which is a good reason to nudge regulars to scan your QR.

The earning rate, plus optional per-vehicle multipliers and busy-hour bonuses, are part of your loyalty configuration — today these ship as sensible defaults, and a settings page to tune them yourself is coming soon.

## The tier ladder

Tiers are ranked by **lifetime points** — they never reset:

- **Bronze** — the starting tier, from 0 points.
- **Silver** — from 500 lifetime points. Perk: **5% off every wash**, applied automatically at the POS.
- **Gold** — from 2,000 lifetime points. Perks: **10% off every wash** plus queue priority flagging.

When a wash tips a customer into a higher tier, they get a congratulations notification on the spot — a real milestone, celebrated the moment it happens.

One honest note: points are a lifetime score that drives tiers. They can't be spent or exchanged — there is no points-redemption catalogue yet.

## What your customers see

Their app shows a loyalty card on the home screen — tier badge, lifetime points, and a progress bar to the next tier — and a full loyalty page with their points balance, the complete tier ladder, and every perk each tier unlocks. The ladder does your marketing for you: _"212 points to Gold"_ sells the next wash better than any poster.

## Vouchers

Vouchers are one-time discounts you hand to specific customers. Build reusable **templates** on the Vouchers page: a name, a discount — **% off** or **RM off** the whole ticket — how many each customer may receive, and how many days each issued voucher lives (30 by default). Templates can be targeted: minimum tier, new customers only, or customers carrying a specific tag. Pause a template any time; already-issued vouchers are untouched.

Templates never fire on their own — **you issue every voucher deliberately**, from two places:

- the **Customers** page — pick a customer, pick a template;
- the **POS** — issue on the spot after a great wash, while the customer is still smiling.

The customer sees the voucher in their app's wallet with its expiry. Redeeming happens at the counter: the cashier picks the voucher on the POS payment sheet and the discount lands as its own line.

## What loyalty doesn't include yet

Being straight with you: there's **no punch card** ("10 washes, 1 free"), **no achievements engine**, and **no birthday rewards** yet — and no dashboard page to edit the points rate or tier thresholds, which currently ship as the defaults described above. What's live is exactly what this page describes: points on collection, the three-tier ladder with automatic discounts, and manually issued vouchers. Related but separate: automatic **promotions** (happy hour, day-of-week) and **prepaid wash packs** have their own tools under the Promos menu — promotions now has its own article in this manual.
